import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import { getSupabaseClient } from "../supabase";
import type { Database, Profile } from "../types/database";
import { decideAccess, type AccessRequirement } from "./access";
import { authBoundaryEventAction } from "./authEventPolicy";

type AuthState =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "error"; message: string }
  | { status: "ready"; profile: Profile };

type AuthBoundaryProps = {
  requirement: AccessRequirement;
  children: (profile: Profile) => ReactNode;
};

async function loadProfile(client: SupabaseClient<Database>, user: User): Promise<AuthState> {
  // A freshly issued Supabase JWT can reach PostgREST a fraction of a second
  // before every gateway clock accepts its iat. Treat that narrow condition as
  // transient so a valid sign-in cannot strand the editor on an error screen.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { data, error } = await client.from("profiles").select("*").eq("id", user.id).single();
    if (!error) return { status: "ready", profile: data };
    if (!/jwt issued at future/i.test(error.message) || attempt === 2) {
      return { status: "error", message: `Profile could not be loaded: ${error.message}` };
    }
    await new Promise((resolve) => window.setTimeout(resolve, 750 * (attempt + 1)));
  }
  return { status: "error", message: "Profile could not be loaded after retrying authentication." };
}

export function AuthBoundary({ requirement, children }: AuthBoundaryProps) {
  const clientResult = useMemo(() => getSupabaseClient(), []);
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    if (!clientResult.ok) return;

    let disposed = false;
    let requestSequence = 0;
    let loadedUserId: string | undefined;
    let resolvingUserId: string | undefined;

    const resolveSession = async (session: Session | null, showLoading: boolean, force = false) => {
      const user = session?.user;
      if (!user) {
        requestSequence += 1;
        loadedUserId = undefined;
        resolvingUserId = undefined;
        if (!disposed) setState({ status: "signed-out" });
        return;
      }

      if (!force && (loadedUserId === user.id || resolvingUserId === user.id)) return;

      const request = ++requestSequence;
      resolvingUserId = user.id;
      if (showLoading && !disposed) setState({ status: "loading" });
      const nextState = await loadProfile(clientResult.client, user);
      if (disposed || request !== requestSequence) return;

      resolvingUserId = undefined;
      loadedUserId = nextState.status === "ready" ? user.id : undefined;
      setState(nextState);
    };

    const { data } = clientResult.client.auth.onAuthStateChange((event, session) => {
      const action = authBoundaryEventAction(event);
      if (action === "ignore") return;
      if (action === "sign-out") {
        void resolveSession(null, false);
        return;
      }

      // Supabase warns against awaiting additional client work inside the auth
      // callback. Defer the profile lookup until the callback has returned.
      queueMicrotask(() => {
        if (action === "resolve-initial") void resolveSession(session, true);
        else if (action === "reload-profile") void resolveSession(session, false, true);
        else void resolveSession(session, loadedUserId === undefined);
      });
    });

    return () => {
      disposed = true;
      requestSequence += 1;
      data.subscription.unsubscribe();
    };
  }, [clientResult]);

  if (!clientResult.ok) {
    return <SetupRequired message={clientResult.message} />;
  }
  if (state.status === "loading") return <StatusPanel title="Opening the realm…" testId="auth-loading" />;
  if (state.status === "signed-out") return <SignIn client={clientResult.client} />;
  if (state.status === "error") return <StatusPanel title="Access could not be verified" detail={state.message} testId="auth-error" />;

  const decision = decideAccess(state.profile, requirement);
  if (!decision.allowed) {
    const message = decision.reason === "not-admin"
      ? "This account is not an authoring administrator."
      : "This account has not been allowlisted for the private game.";
    return <AccessDenied message={message} onSignOut={() => clientResult.client.auth.signOut()} />;
  }

  return <>{children(state.profile)}</>;
}

function SignIn({ client }: { client: SupabaseClient<Database> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setMessage(undefined);
    const { error } = await client.auth.signInWithPassword({ email, password });
    if (error) setMessage(error.message);
    setSubmitting(false);
  }

  return (
    <main className="access-page" data-testid="sign-in">
      <form className="access-card" onSubmit={submit}>
        <span className="access-kicker">The Silver Realm</span>
        <h1>Enter Lumenhall</h1>
        <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
        <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
        {message && <p className="access-error" role="alert">{message}</p>}
        <button type="submit" disabled={submitting}>{submitting ? "Verifying…" : "Continue"}</button>
      </form>
    </main>
  );
}

function AccessDenied({ message, onSignOut }: { message: string; onSignOut: () => Promise<unknown> }) {
  return (
    <main className="access-page" data-testid="access-denied">
      <section className="access-card"><h1>Access restricted</h1><p>{message}</p><button onClick={() => void onSignOut()}>Use another account</button></section>
    </main>
  );
}

function SetupRequired({ message }: { message: string }) {
  return <StatusPanel title="Supabase setup required" detail={`${message}. Copy .env.example to .env.local and add the project values.`} testId="setup-required" />;
}

function StatusPanel({ title, detail, testId }: { title: string; detail?: string; testId: string }) {
  return <main className="access-page" data-testid={testId}><section className="access-card"><h1>{title}</h1>{detail && <p>{detail}</p>}</section></main>;
}
